import { getCurrentAudioId } from '../../../store/current-video/selectors';
import { getState } from '../../../store';
import { AudioPlayerProvider } from './audioPlayerProvider';
import { ICaptionsItem } from '../../../types/common';
import { TextPlayerProvider } from './textPlayerProvider';
import { getActiveGroupTargetLanguage, getAudioById } from '../../../store/models/selectors';
import { TAudio, TAudioType } from '../../../store/models/types';

export interface IPlayerProvider {
  play(time?: number, text?: string): Promise<void>;
  getCurrentTime(): Promise<number>;
  pause(): void;
  seek(): void;
  getTargetCaptions(): ICaptionsItem[];
  getActiveTargetCaptionIndex(): Promise<number>;
  isPaused(): boolean;
  isPlay(): boolean;
  isCanPlayFromTime(): boolean;
  isFinished(): boolean;
  isTextAudioMode(): boolean;
}

export interface IPlayerProviderHandler {
  play(time?: number): Promise<void>;
  getCurrentTime(): Promise<number>;
  pause(): void;
  seek(time?: number): void;
  getTargetCaptions(): ICaptionsItem[];
  getActiveTargetCaptionIndex(): Promise<number>;
  isPaused(): boolean;
  isPlay(): boolean;
}

const audioProvider = new AudioPlayerProvider();
const textProvider = new TextPlayerProvider();

class PlayerProviderImpl implements IPlayerProvider {

  public getActiveTargetCaptionIndex(): Promise<number> {
    if (this.isTextAudioMode()) {
      return textProvider.getActiveTargetCaptionIndex();
    }
    return this.getHandler().getActiveTargetCaptionIndex();
  }

  public async getCurrentTime(): Promise<number> {
    if (this.isTextAudioMode()) {
      return 0;
    }
    return this.getHandler().getCurrentTime();
  }

  public getTargetCaptions(): ICaptionsItem[] {
    if (this.isTextAudioMode()) {
      return textProvider.getTargetCaptions();
    }
    return this.getHandler().getTargetCaptions();
  }

  public isPaused(): boolean {
    if (this.isTextAudioMode()) {
      return textProvider.isPaused();
    }
    return this.getHandler().isPaused();
  }

  public isPlay(): boolean {
    if (this.isTextAudioMode()) {
      return textProvider.isPlay();
    }
    return this.getHandler().isPlay();
  }

  public pause(): void {
    if (this.isTextAudioMode()) {
      textProvider.pause();
    }
    this.getHandler().pause();
  }

  public async play(time?: number, text?: string): Promise<void> {
    if (this.isTextAudioCurrentOpen() || this.isNativeAudioPhrasesMode()) {
      const audio = this.getAudio();
      const langCode = audio ? audio.language?.code : getActiveGroupTargetLanguage(getState())?.code;
      await textProvider.play(text, langCode)
    } else {
      await this.getHandler().play(time)
    }
  }

  public seek(time?: number): void {
    if (!this.isTextAudioMode()) {
      this.getHandler().seek(time);
    }
  }

  public isCanPlayFromTime(): boolean {
    return !this.isTextAudioMode();
  }

  public isFinished(): boolean {
    if (this.isTextAudioMode()) {
      return textProvider.isFinished();
    }
    return false;
  }

  private getHandler(): IPlayerProviderHandler {
    return audioProvider;
  }

  private getAudio(): TAudio | null | undefined {
    const state = getState();
    const audioId = getCurrentAudioId(state);
    return audioId ? getAudioById(state, audioId) : null;
  }

  private isTextAudioCurrentOpen() {
    const audio = this.getAudio();
    return !!audio && audio.type === TAudioType.TEXT;
  }

  private isNativeAudioPhrasesMode(): boolean {
    /*const nativeAudioModes = [EPhraseListMode.ALL, EPhraseListMode.GROUP];
    const phraseListMode = PhraseListSelectors.getListMode(getState());
    return nativeAudioModes.includes(phraseListMode);*/
    return false;
  }

  public isTextAudioMode(): boolean {
    return this.isTextAudioCurrentOpen() || this.isNativeAudioPhrasesMode();
  }

}

export class PlayerProvider {

  private static providerInst = new PlayerProviderImpl();

  public static getProvider(): IPlayerProvider {
    return this.providerInst;
  }

}
